import React from 'react'
import { observer } from 'mobx-react-lite'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'

import { CategoryIcon, Text } from '@mxenabled/mxui'
import {
  Cancel as CancelIcon,
  ChevronRight as ChevronRightIcon,
  Delete as DeleteOutlinedIcon,
} from '@mxenabled/mx-icons'

import { CategorySelectorDrawer } from '../../../categories'

import { CurrencyInput, useGlobalCopyStore } from '../../../common'
import type { Transaction } from '../../../core'

import { useEvent } from '../../../common'
import { ON_CLICK_EVENTS } from '../../constants/OnClickEvents'
import { formatCurrency } from '../../../core/utils/NumberFormatting'

interface SplitRowProps {
  index: number
  onCategoryChanged: (index: number, categoryGuid: string) => void
  onDeleteRow: (index: number) => void
  onUpdateAmount: (index: number, amount: number) => void
  split: Transaction
}

const SplitRow: React.FC<SplitRowProps> = ({
  index,
  onDeleteRow,
  onCategoryChanged,
  onUpdateAmount,
  split,
}) => {
  const { onEvent } = useEvent()
  const { transactions: copy } = useGlobalCopyStore()

  const [amount, setAmount] = React.useState(`${split.amount}`)
  const [categorySelectorOpen, setCategorySelectorOpen] = React.useState(false)

  const isTopRow = index === 0

  const handleChangeAmount = (value: string) => {
    if (!isNaN(Number(value))) {
      setAmount(value)
      onUpdateAmount(index, Number(value))
    }
  }

  return (
    <Box bgcolor="background.paper" className="mx-txn-split-manager-row" pb={4} px={8}>
      <Grid alignItems={isTopRow ? 'center' : 'start'} container={true}>
        <Grid size={7}>
          <Stack alignItems="start">
            <Button
              endIcon={<ChevronRightIcon />}
              onClick={() => setCategorySelectorOpen(true)}
              startIcon={<CategoryIcon categoryGuid={split.top_level_category_guid} />}
              sx={{ px: 12, py: 8 }}
            >
              {split.category}
            </Button>
            <Box>
              {!split.category_guid && (
                <Stack color="error.main" direction="row" gap={8} p={12}>
                  <span aria-label="error" role="img">
                    <CancelIcon sx={{ fontSize: 15 }} />
                  </span>
                  <Text variant="body2">{copy.split_transaction_errors_select_category}</Text>
                </Stack>
              )}
            </Box>
          </Stack>
        </Grid>
        <Grid size={5}>
          {isTopRow ? (
            <div className="mx-txn-split-manager-row-top-container">
              <Text>{formatCurrency(split.amount, '0,0.00')}</Text>
            </div>
          ) : (
            <Stack alignItems="start">
              <Stack alignItems="start" direction="row" gap={8}>
                <CurrencyInput
                  amount={amount}
                  ariaLabel={copy.split_transaction_edit_split_amount}
                  label={copy.split_transaction_edit_split_amount}
                  minAmount={0.01}
                  name="split-amount-input"
                  setAmount={handleChangeAmount}
                  sx={{
                    '.MuiOutlinedInput-input': { p: 12 },
                  }}
                  type="number"
                />
                <IconButton
                  aria-label={copy.split_transaction_delete_split_btn}
                  color="secondary"
                  onClick={() => {
                    onEvent(ON_CLICK_EVENTS.ON_TRANSACTION_SPLIT_DELETE_CLICK)
                    onDeleteRow(index)
                  }}
                  sx={{ w: 44, h: 44 }}
                >
                  <DeleteOutlinedIcon />
                </IconButton>
              </Stack>
              {!amount && (
                <Stack color="error.main" direction="row" gap={8} py={12}>
                  <span aria-label="error" role="img">
                    <CancelIcon sx={{ fontSize: 15 }} />
                  </span>
                  <Text variant="body2">{copy.split_transaction_errors_enter_an_amount}</Text>
                </Stack>
              )}
            </Stack>
          )}
        </Grid>
      </Grid>

      <CategorySelectorDrawer
        initialSelected={split.category_guid}
        onClose={() => setCategorySelectorOpen(false)}
        onSelect={(guid: string) => {
          onCategoryChanged(index, guid)
          setCategorySelectorOpen(false)
        }}
        showDrawer={categorySelectorOpen}
        title={copy.actions_select_a_category}
      />
    </Box>
  )
}

export default observer(SplitRow)
